import { Grid, Table } from '@statsbomb/kitbag-components';
import {
  useVideoCanPlay,
  useVideoControls,
  VideoFull,
} from '@statsbomb/video-client-js';
import { useAtom, useSetAtom } from 'jotai';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { videoInfoPropTypes } from '../../types/videoInfo';
import { videoReferencePropTypes } from '../../types/videoReference';
import {
  microsecondsToTimeStamp,
  timestampAsMicroseconds,
} from '../../utils/time';
import OffsetData from '../OffsetData';
import ReviewOffsetsStepTableRows from './steps/ReviewOffsetsStepTableRows';
import { GridContainer, ModalContent } from './steps/steps.styles';
import { updateVideoInfoInVideosRequiringAlignmentCheckAtom } from '../../atoms/videoAlignmentRequired';
import { VALUE_IDENTIFYING_NOT_SHOWN } from '../OffsetData/OffsetData';
import { updateVideoSearchResultsAtom } from '../../atoms/videoListings';
import {
  draftPeriodStartAtom,
  draftPeriodEndAtom,
} from '../../atoms/videoAlignmentCheck';

const handleSetPeriod = (value, setPeriod) => {
  setPeriod(
    value
      ? timestampAsMicroseconds(value, { includesHours: true })
      : VALUE_IDENTIFYING_NOT_SHOWN,
  );
};

const toTimeStamp = (microseconds) =>
  microseconds && microseconds > 1
    ? microsecondsToTimeStamp(microseconds, { includeDecimals: true })
    : null;

export function ReviewOffsets({ videoInfo, videoReference }) {
  const intl = useIntl();
  const { setCurrentTime } = useVideoControls();
  const canPlay = useVideoCanPlay();

  const [periodStart, setPeriodStart] = useAtom(draftPeriodStartAtom);
  const [periodEnd, setPeriodEnd] = useAtom(draftPeriodEndAtom);

  const updateVideoInfoInVideosRequiringAlignmentCheck = useSetAtom(
    updateVideoInfoInVideosRequiringAlignmentCheckAtom,
  );
  const updateVideoSearchResults = useSetAtom(updateVideoSearchResultsAtom);
  const [periodStartNotShown, setPeriodStartNotShown] = useState(
    videoInfo?.periodStart === VALUE_IDENTIFYING_NOT_SHOWN,
  );
  const [periodEndNotShown, setPeriodEndNotShown] = useState(
    videoInfo?.periodEnd === VALUE_IDENTIFYING_NOT_SHOWN,
  );

  const handleReset = () => {
    setPeriodStart(videoInfo?.periodStart);
    setPeriodEnd(videoInfo?.periodEnd);
  };

  useEffect(() => {
    if (!periodStart) setPeriodStart(videoInfo?.periodStart || null);
    if (!periodEnd) setPeriodEnd(videoInfo?.periodEnd || null);
  }, [videoInfo]);

  useEffect(() => {
    if (canPlay) {
      setCurrentTime(videoInfo?.periodStart);
    }
  }, [canPlay, videoInfo?.periodStart]);

  const handleUpdateVideoRequiringAlignmentCheck = () => {
    updateVideoInfoInVideosRequiringAlignmentCheck(videoReference, {
      periodEnd,
      periodStart,
    });

    updateVideoSearchResults(videoReference, {
      kickoff: periodStart,
      period_end: periodEnd,
    });
  };

  const handleSetPeriodStart = (value) => {
    handleSetPeriod(value, setPeriodStart);
  };

  const handleSetPeriodEnd = (value) => {
    handleSetPeriod(value, setPeriodEnd);
  };

  return (
    <ModalContent>
      <Grid>
        <Grid item xs={12} lg={6}>
          <VideoFull videoReference={videoReference} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <GridContainer>
            <Table testId="review-offsets-match-overview-table">
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>
                    {intl.formatMessage({
                      defaultMessage: 'Match Overview',
                      id: '7558d',
                    })}
                  </Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Head>
              <Table.Body>
                <ReviewOffsetsStepTableRows
                  videoInfo={videoInfo}
                  videoReference={videoReference}
                />
              </Table.Body>
            </Table>
            <OffsetData
              lastUpdatedByIsHidden
              reset={handleReset}
              periodStart={toTimeStamp(periodStart)}
              periodEnd={toTimeStamp(periodEnd)}
              setPeriodStart={handleSetPeriodStart}
              setPeriodEnd={handleSetPeriodEnd}
              setPeriodStartNotShown={setPeriodStartNotShown}
              setPeriodEndNotShown={setPeriodEndNotShown}
              periodStartNotShown={periodStartNotShown}
              periodEndNotShown={periodEndNotShown}
              videoReference={{
                ...videoReference,
                matchPart: videoReference?.period,
              }}
              onSave={handleUpdateVideoRequiringAlignmentCheck}
            />
          </GridContainer>
        </Grid>
      </Grid>
    </ModalContent>
  );
}

ReviewOffsets.propTypes = {
  videoInfo: PropTypes.shape(videoInfoPropTypes).isRequired,
  videoReference: PropTypes.shape(videoReferencePropTypes).isRequired,
};

export default { ReviewOffsetsModalContent: ReviewOffsets };
